import React, { useEffect } from 'react';
import { Box, Grid, TextField, Switch, FormControlLabel, Typography, Divider, Paper } from '@mui/material';

const SalaryDetails = ({ formData, handleChange, setFormData }) => {

  // Real-time Calculation Engine (Syncs directly to Parent's formData)
  useEffect(() => {
    let ctc = parseFloat(formData.ctc) || 0;
    let basic = parseFloat(formData.basic) || 0;
    let hra = parseFloat(formData.hra) || 0;
    let epf_er = parseFloat(formData.epf_employer) || 0;
    let epf_ee = parseFloat(formData.epf_employee) || 0;
    let grat_er = parseFloat(formData.gratuity_employer) || 0;
    let esic_er = parseFloat(formData.esic_employer) || 0;
    let esic_ee = parseFloat(formData.esic_employee) || 0;
    let prof_tax = parseFloat(formData.prof_tax) || 0;
    let tds = parseFloat(formData.tds) || 0;

    let updates = {};

    if (formData.auto_basic_hra) {
      basic = Math.round(ctc * 0.30);
      hra = Math.round(basic * 0.40);
      if ((parseFloat(formData.basic) || 0) !== basic) updates.basic = basic;
      if ((parseFloat(formData.hra) || 0) !== hra) updates.hra = hra;
    }

    if (formData.auto_epf) {
      epf_er = Math.round(basic * 0.13);
      epf_ee = Math.round(basic * 0.12);
      if ((parseFloat(formData.epf_employer) || 0) !== epf_er) updates.epf_employer = epf_er;
      if ((parseFloat(formData.epf_employee) || 0) !== epf_ee) updates.epf_employee = epf_ee;
    }

    if (formData.auto_gratuity) {
      grat_er = Math.round(basic * 0.0481);
      if ((parseFloat(formData.gratuity_employer) || 0) !== grat_er) updates.gratuity_employer = grat_er;
    }

    if (formData.auto_esic) {
      let esic_base = ctc - epf_er - grat_er;
      esic_er = Math.round(esic_base * 0.0325);
      esic_ee = Math.round(esic_base * 0.0075);
      if ((parseFloat(formData.esic_employer) || 0) !== esic_er) updates.esic_employer = esic_er;
      if ((parseFloat(formData.esic_employee) || 0) !== esic_ee) updates.esic_employee = esic_ee;
    }

    let special_allowance = Math.round(ctc - basic - hra - (epf_er + esic_er + grat_er));
    if (special_allowance < 0) special_allowance = 0; 
    let gross = Math.round(basic + hra + special_allowance);
    let net_salary = Math.round(gross - epf_ee - esic_ee - prof_tax - tds);

    if ((parseFloat(formData.special_allowance) || 0) !== special_allowance) updates.special_allowance = special_allowance;
    if ((parseFloat(formData.gross) || 0) !== gross) updates.gross = gross;
    if ((parseFloat(formData.net_salary) || 0) !== net_salary) updates.net_salary = net_salary;

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line
  }, [
    formData.ctc, formData.basic, formData.hra, 
    formData.epf_employer, formData.gratuity_employer, 
    formData.auto_basic_hra, formData.auto_epf, formData.auto_esic, formData.auto_gratuity,
    formData.prof_tax, formData.tds
  ]);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: '#fbfbfb', border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth required label="Monthly CTC" name="ctc" type="number" value={formData.ctc} onChange={handleChange} sx={{ bgcolor: 'white' }} />
          </Grid>
          <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel control={<Switch checked={formData.auto_basic_hra} onChange={handleChange} name="auto_basic_hra" color="primary" />} label="Auto Basic & HRA" />
            <FormControlLabel control={<Switch checked={formData.auto_epf} onChange={handleChange} name="auto_epf" color="primary" />} label="Auto EPF" />
            <FormControlLabel control={<Switch checked={formData.auto_esic} onChange={handleChange} name="auto_esic" color="primary" />} label="Auto ESIC" />
            <FormControlLabel control={<Switch checked={formData.auto_gratuity} onChange={handleChange} name="auto_gratuity" color="primary" />} label="Auto Gratuity" />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>Earnings</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Basic" name="basic" type="number" value={formData.basic} onChange={handleChange} disabled={formData.auto_basic_hra} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="HRA" name="hra" type="number" value={formData.hra} onChange={handleChange} disabled={formData.auto_basic_hra} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Special Allowance" name="special_allowance" value={formData.special_allowance} disabled sx={{ bgcolor: '#f0f0f0' }} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Gross" name="gross" value={formData.gross} disabled sx={{ bgcolor: '#e8f5e9', '& input': { fontWeight: 'bold' } }} /></Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>Employer Contributions</Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}><TextField fullWidth label="EPF (Employer)" name="epf_employer" type="number" value={formData.epf_employer} onChange={handleChange} disabled={formData.auto_epf} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="ESIC (Employer)" name="esic_employer" type="number" value={formData.esic_employer} onChange={handleChange} disabled={formData.auto_esic} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Gratuity" name="gratuity_employer" type="number" value={formData.gratuity_employer} onChange={handleChange} disabled={formData.auto_gratuity} /></Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Employee Deductions & Take Home</Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}><TextField fullWidth label="EPF" name="epf_employee" type="number" value={formData.epf_employee} onChange={handleChange} disabled={formData.auto_epf} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="ESIC" name="esic_employee" type="number" value={formData.esic_employee} onChange={handleChange} disabled={formData.auto_esic} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="Prof. Tax" name="prof_tax" type="number" value={formData.prof_tax} onChange={handleChange} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="TDS" name="tds" type="number" value={formData.tds} onChange={handleChange} /></Grid>
            
            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Net Salary (Take Home)" name="net_salary" value={formData.net_salary} disabled sx={{ bgcolor: '#e3f2fd', '& input': { fontWeight: 'bold', fontSize: '1.2rem', color: '#1565c0' } }} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalaryDetails;